// Store session token for cross-origin auth
const SESSION_STORAGE_KEY = 'brainmap_session';
const DEBUG = import.meta.env.DEV;

/**
 * Pull the session token out of the URL fragment (`#session=…`) or, for
 * backwards compatibility, the query string (`?session=…`). Both forms are
 * stripped from the visible URL immediately via history.replaceState() so
 * the token doesn't survive in browser history or analytics scripts.
 *
 * Returns true if a token was found and persisted.
 */
function readSessionFromLocation(): string | null {
  if (typeof window === 'undefined') return null;

  // Prefer fragment (the new transport — never sent to servers).
  // location.hash includes the leading "#".
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (hash) {
    const hashParams = new URLSearchParams(hash);
    const fromHash = hashParams.get('session');
    if (fromHash) return fromHash;
  }

  // Legacy: query string. Kept so already-issued links keep working until
  // they expire (15 minutes), then this branch becomes dead code.
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get('session');
}

function cleanLocationOfSession() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('session');
  // Strip fragment entirely — we only ever put session=… there.
  url.hash = '';
  window.history.replaceState({}, '', url.pathname + url.search);
}

/**
 * Extract session from URL — call this early in app lifecycle.
 * Returns true if session was found and stored.
 */
export function extractSessionFromUrl(): boolean {
  const token = readSessionFromLocation();
  if (token) {
    if (DEBUG) console.log('[Session] Extracted from URL');
    localStorage.setItem(SESSION_STORAGE_KEY, token);
    cleanLocationOfSession();
    return true;
  }
  return false;
}

// Also run immediately on module load so the token is persisted even if the
// app code hasn't called extractSessionFromUrl() yet.
const initialToken = readSessionFromLocation();
if (initialToken) {
  if (DEBUG) console.log('[Session] Extracted from URL at load');
  localStorage.setItem(SESSION_STORAGE_KEY, initialToken);
  cleanLocationOfSession();
} else if (DEBUG) {
  console.log('[Session] No session in URL at load');
}

export function useSessionToken(): string | null {
  // Just for React hooks compatibility - return what's in storage
  return localStorage.getItem(SESSION_STORAGE_KEY);
}

export function getSessionToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SESSION_STORAGE_KEY);
}

export function clearSessionToken() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

export function createAuthHeaders(): HeadersInit {
  const session = getSessionToken();
  if (session) {
    if (DEBUG) console.log('[Session] Using Bearer token');
    return { 'Authorization': `Bearer ${session}` };
  }
  if (DEBUG) console.log('[Session] No token in localStorage');
  return {};
}